// Life & Wealth Solutions System Platform - System Integrity Widget
// Autonomic System Health Display for Agent 12 Dashboard
// Part of Mynd My Business in a Box™

import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface SystemIntegrityMetrics {
  total_fixes_applied: number;
  total_escalations: number;
  mean_time_to_remediate_seconds: number;
  success_rate_percent: number;
  developer_hours_saved: number;
  estimated_cost_avoided_usd: number;
  zero_downtime_deployments: number;
}

interface SystemIntegrityReport {
  reporting_period: {
    start: string;
    end: string;
  };
  system_health_score: number;
  autonomic_self_healing_events: {
    total_fixes_performed: number;
    mean_time_to_remediate_seconds: number;
    critical_patches_applied: number;
  };
  ml_drift_calibration: {
    logic_alignment_checks: number;
    bias_corrections: number;
    model_retrained: boolean;
  };
  operational_continuity_savings: {
    developer_hours_saved: number;
    estimated_cost_avoided: number;
    zero_downtime_deployments: number;
  };
  recent_events: Array<{
    timestamp: string;
    action: string;
    details: string;
  }>;
}

const SystemIntegrityWidget: React.FC = () => {
  const [metrics, setMetrics] = useState<SystemIntegrityMetrics | null>(null);
  const [healthScore, setHealthScore] = useState<number>(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadSystemIntegrity();
    const interval = setInterval(loadSystemIntegrity, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadSystemIntegrity = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      // Load health score
      const healthResponse = await axios.get(`${API_URL}/system-integrity/health-score`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHealthScore(healthResponse.data.health_score);

      // Load full metrics
      const metricsResponse = await axios.get(`${API_URL}/system-integrity/metrics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMetrics(metricsResponse.data.metrics);

      setLoading(false);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load system integrity:', err);
      setError(err.response?.data?.detail || 'Failed to load system integrity');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 p-6">
        <div className="flex items-center justify-center h-48">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 rounded-lg border border-red-200 p-6">
        <h3 className="text-red-800 font-semibold mb-2">System Integrity Error</h3>
        <p className="text-red-600 text-sm">{error}</p>
      </div>
    );
  }

  const getHealthColor = (score: number) => {
    if (score >= 95) return 'text-green-600';
    if (score >= 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getHealthBadge = (score: number) => {
    if (score >= 95) return 'bg-green-100 text-green-800 border-green-200';
    if (score >= 80) return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  return (
    <div className="bg-gradient-to-br from-purple-50 to-blue-50 rounded-lg border border-purple-200 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="bg-purple-600 rounded-full p-2">
            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">⚡ Autonomic System Integrity</h3>
            <p className="text-sm text-gray-600">Self-Healing Nervous System</p>
          </div>
        </div>
        <button
          onClick={loadSystemIntegrity}
          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
        >
          🔄 Refresh
        </button>
      </div>

      {/* System Health Score */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">System Health Score</span>
          <span className={`text-3xl font-bold ${getHealthColor(healthScore)}`}>
            {healthScore.toFixed(1)}%
          </span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-3">
          <div
            className={`h-3 rounded-full ${
              healthScore >= 95 ? 'bg-green-500' : healthScore >= 80 ? 'bg-yellow-500' : 'bg-red-500'
            }`}
            style={{ width: `${healthScore}%` }}
          ></div>
        </div>
        <div className={`mt-2 inline-flex items-center px-3 py-1 rounded-full border text-xs font-semibold ${getHealthBadge(healthScore)}`}>
          {healthScore >= 95 ? '✅ Optimal Health' : healthScore >= 80 ? '⚠️ Attention Required' : '🚨 Critical'}
        </div>
      </div>

      {/* Key Metrics Grid */}
      {metrics && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          {/* Self-Fixes Applied */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Self-Fixes Applied</div>
            <div className="text-2xl font-bold text-blue-600">{metrics.total_fixes_applied}</div>
            <div className="text-xs text-gray-500 mt-1">
              ⚡ {metrics.mean_time_to_remediate_seconds.toFixed(1)}s avg MTTR
            </div>
          </div>

          {/* Cost Savings */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Cost Avoided</div>
            <div className="text-2xl font-bold text-green-600">
              ${metrics.estimated_cost_avoided_usd.toLocaleString()}
            </div>
            <div className="text-xs text-gray-500 mt-1">
              💰 {metrics.developer_hours_saved.toFixed(1)} dev hours saved
            </div>
          </div>

          {/* Zero-Downtime Deployments */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Hot-Fixes Deployed</div>
            <div className="text-2xl font-bold text-purple-600">{metrics.zero_downtime_deployments}</div>
            <div className="text-xs text-gray-500 mt-1">🚀 Zero downtime</div>
          </div>

          {/* Success Rate */}
          <div className="bg-white rounded-lg p-4 border border-gray-200">
            <div className="text-sm text-gray-600 mb-1">Success Rate</div>
            <div className="text-2xl font-bold text-indigo-600">{metrics.success_rate_percent.toFixed(1)}%</div>
            <div className="text-xs text-gray-500 mt-1">
              📊 {metrics.total_escalations} escalations
            </div>
          </div>
        </div>
      )}

      {/* 2026 AI-Native Moat Badge */}
      <div className="bg-gradient-to-r from-purple-600 to-blue-600 rounded-lg p-4 text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold mb-1">🛡️ 2026 AI-Native Moat</div>
            <div className="text-xs opacity-90">
              Autonomic Computing • ML Self-Healing • Zero-Downtime Governance
            </div>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">✅</div>
            <div className="text-xs">Active</div>
          </div>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-4 pt-4 border-t border-purple-200">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Powered by My Sentinel + Autonomic Watchdog</span>
          <span className="text-purple-600 font-medium">Real-time monitoring</span>
        </div>
      </div>
    </div>
  );
};

export default SystemIntegrityWidget;
